import dayjs from 'dayjs';
import { MINUTES_IN_DAY, MINUTES_IN_HOUR } from './constants';
import {
  EDaysOfWeek,
  TDailyAvailabilityItem,
} from '@/features/DailyAvailability/types';
import { TNullable, TPeriodCamel } from '@/types';
import { IFormattedTimezone, TDateFormat, TTimeView } from '@/types/date';
import { TOffset, TListTimezoneItemCamel } from '@/types/list';
import 'dayjs/locale/en';
import { IReportFilterParams } from '@/types/reports';

const MINUTES_PER_DAY = 1440;
const MINUTES_PER_WEEK = 10080;

/**
 * Wraps a time in minutes to a value within the same week.
 * If the time is at the start of the week, it will wrap to the end of the previous week.
 * If the time is at the end of the week, it will wrap to the start of the next week.
 * @param minutes - Time in minutes
 * @returns - Wrapped time in minutes
 */
function wrapWeek(minutes: number): number {
  return ((minutes % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) % MINUTES_PER_WEEK;
}

/**
 * Formats a time in minutes into a string in the format "HH:mm".
 * @param minutesInDay - Time in minutes
 * @returns - Time string in the format "HH:mm"
 */
function formatTime(minutesInDay: number): string {
  const h = Math.floor(minutesInDay / 60);
  const m = minutesInDay % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Converts a string representation of time (HH:mm) into a Dayjs date object
 * using the current day and the specified hour and minute.
 * @param time - Time string in the format "HH:mm"
 * @returns Dayjs object representing today with the specified time
 */
export const convertStringTimeToDate = (time: string) => {
  const [hour, minute] = time.split(':');
  return dayjs().hour(+hour).minute(+minute);
};

const daysMap: Record<string, EDaysOfWeek> = {
  sunday: EDaysOfWeek.Sunday,
  monday: EDaysOfWeek.Monday,
  tuesday: EDaysOfWeek.Tuesday,
  wednesday: EDaysOfWeek.Wednesday,
  thursday: EDaysOfWeek.Thursday,
  friday: EDaysOfWeek.Friday,
  saturday: EDaysOfWeek.Saturday,
};

/**
 * Formats an array of period items into a daily availability object.
 * Groups periods by day of the week and maps them to the corresponding day key.
 * @param items - Array of period objects containing `day`, `startTime`, and `endTime`
 * @returns TDailyAvailabilityItem - Object with each day of the week as keys and arrays of time periods
 */
export const formatDailyAvailability = (
  items: TPeriodCamel[],
): TDailyAvailabilityItem => {
  const defaultDailyAvailability: TDailyAvailabilityItem = {
    [EDaysOfWeek.Monday]: [],
    [EDaysOfWeek.Tuesday]: [],
    [EDaysOfWeek.Wednesday]: [],
    [EDaysOfWeek.Thursday]: [],
    [EDaysOfWeek.Friday]: [],
    [EDaysOfWeek.Saturday]: [],
    [EDaysOfWeek.Sunday]: [],
  };

  return items.reduce((acc, { day, startTime, endTime }) => {
    const mappedDay = daysMap[day.toLowerCase()];
    if (mappedDay && acc[mappedDay]) {
      acc[mappedDay].push({
        startTime,
        endTime,
      });
    }
    return acc;
  }, defaultDailyAvailability);
};

/**
 * Returns the time zone offset in minutes for a given user time zone and date.
 * Falls back to the browser's offset if the user time zone is not found.
 * @param userTimeZoneId - ID of the user's time zone
 * @param timezones - List of available time zones with offsets
 * @param date - Date to calculate the offset for (default is current date)
 * @returns Time zone offset in minutes
 */
export const getTimeZoneOffsetInMinutes = (
  userTimeZoneId: string = '',
  timezones: TListTimezoneItemCamel[],
  date: TDateFormat = dayjs(),
) => {
  const convertDate = dayjs(date);
  const findOffsets = timezones?.find(
    ({ id }) => id === userTimeZoneId,
  )?.offsets;
  const browserOffsetInMinutes = getBrowserOffsetInMinutes();
  if (!findOffsets) return browserOffsetInMinutes;
  return (
    findCurrentOffsetItem(findOffsets, convertDate)?.offset ??
    browserOffsetInMinutes
  );
};

/**
 * Shifts weekly availability periods by a given offset in minutes.
 * Handles day overflow, splits periods across days if necessary, sorts, and merges overlapping periods.
 * @param availability - Array of TPeriodCamel objects containing `day`, `startTime`, and `endTime`
 * @param offset - Number of minutes to shift each period
 * @returns TPeriodCamel[] - New array of availability periods with offset applied, sorted and merged
 */
export function offsetWeeklyAvailability(
  availability: TPeriodCamel[],
  offset: number,
): TPeriodCamel[] {
  // Used to convert days to numbers and vice-versa.
  const dayMap = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  const nAvailability = [];

  for (const entry of availability) {
    const [sHours, sMinutes] = entry.startTime.split(':').map(Number);
    const [eHours, eMinutes] = entry.endTime.split(':').map(Number);

    const dayIndex = dayMap.indexOf(entry.day);
    const base = dayIndex * MINUTES_PER_DAY;

    const newStart = wrapWeek(base + sHours * 60 + sMinutes + offset);
    const newEnd = wrapWeek(base + eHours * 60 + eMinutes + offset);

    const startDay = Math.floor(newStart / MINUTES_PER_DAY);
    const endDay = Math.floor(newEnd / MINUTES_PER_DAY);

    if (startDay !== endDay) {
      nAvailability.push({
        day: dayMap[startDay],
        startTime: formatTime(newStart % MINUTES_PER_DAY),
        endTime: '23:59',
      });

      if (newEnd % MINUTES_PER_DAY !== 0) {
        nAvailability.push({
          day: dayMap[endDay],
          startTime: '00:00',
          endTime: formatTime(newEnd % MINUTES_PER_DAY),
        });
      }

      continue;
    }

    nAvailability.push({
      day: dayMap[startDay],
      startTime: formatTime(newStart % MINUTES_PER_DAY),
      endTime: formatTime(newEnd % MINUTES_PER_DAY),
    });
  }

  // Sort availability.
  nAvailability.sort((a, b) => {
    const aDay = dayMap.indexOf(a.day);
    const bDay = dayMap.indexOf(b.day);

    if (aDay !== bDay) {
      return aDay - bDay;
    }

    const aStart = a.startTime.split(':').map(Number);
    const bStart = b.startTime.split(':').map(Number);

    const taStart = aStart[0] * 60 + aStart[1];
    const tbStart = bStart[0] * 60 + bStart[1];

    return taStart - tbStart;
  });

  // Merge availability in same days.
  const mnAvailability = [];
  for (const a in nAvailability) {
    const entry = nAvailability[a];
    const previousEntry = mnAvailability[mnAvailability.length - 1];

    if (!previousEntry) {
      // Nothing yet.
      mnAvailability.push(entry);
      continue;
    }

    if (previousEntry.day !== entry.day) {
      mnAvailability.push(entry);
      continue; // Different day.
    }

    const pStart = previousEntry.startTime.split(':').map(Number);
    const pEnd = previousEntry.endTime.split(':').map(Number);

    const eStart = entry.startTime.split(':').map(Number);
    const eEnd = entry.endTime.split(':').map(Number);

    const tpStart = pStart[0] * 60 + pStart[1];
    const tpEnd = pEnd[0] * 60 + pEnd[1];
    const teStart = eStart[0] * 60 + eStart[1];
    const teEnd = eEnd[0] * 60 + eEnd[1];

    // Overlap inside.
    if (teStart >= tpStart && teEnd <= tpEnd) {
      continue;
    }

    // Remove negative and 1 minute gaps.
    if (teStart - tpEnd < 2) {
      previousEntry.endTime = entry.endTime;
      continue;
    }

    mnAvailability.push(entry);
  }

  // Filter out all empty days.
  return mnAvailability;
}

/**
 * Returns the current time in a timezone. A missing timezone is treated as UTC,
 * the same as on the backend.
 * @param timezone - ID of the time zone
 * @param timezones - List of available time zones with offsets
 * @returns The current time with the timezone's offset, or null while the time zone list
 * is not loaded, as the offset would otherwise fall back to the browser's one
 */
export const getNowInTimezone = (
  timezone: string | undefined,
  timezones: TListTimezoneItemCamel[],
): TNullable<dayjs.Dayjs> => {
  if (!timezone) return dayjs().utcOffset(0);
  if (!timezones.length) return null;
  return dayjs().utcOffset(getTimeZoneOffsetInMinutes(timezone, timezones));
};

/**
 * Converts a wall time on a date's day in a timezone to an instant. Each wall time gets
 * the offset in force at it, so a time after a daylight saving time change is not an hour
 * off. Mirrors the backend: a wall time that happens twice, when clocks go back, resolves
 * to its first occurrence, and one that does not exist, when clocks go forward, resolves
 * to the instant the clocks change.
 * @param date - A date in the timezone whose day is used, see getNowInTimezone
 * @param time - Time string in the format "HH:mm"
 * @param timezone - ID of the time zone
 * @param timezones - List of available time zones with offsets
 * @returns The instant of the wall time with the offset in force at it
 */
export const getWallTimeInTimezone = (
  date: dayjs.Dayjs,
  time: string,
  timezone: string | undefined,
  timezones: TListTimezoneItemCamel[],
): dayjs.Dayjs => {
  const [hour, minute] = time.split(':').map(Number);
  // The wall time on the date's day, encoded as if it were UTC.
  const naive = dayjs.utc(date.format('YYYY-MM-DD')).hour(hour).minute(minute);
  const offsets = timezones.find(({ id }) => id === timezone)?.offsets;

  if (!offsets?.length) {
    return naive
      .subtract(date.utcOffset(), 'minute')
      .utcOffset(date.utcOffset());
  }

  // An offset applies from its date on, the date itself included.
  const offsetItemAt = (instant: dayjs.Dayjs) =>
    offsets.findLast((item) => !instant.isBefore(item.date)) ?? offsets[0];
  const offsetAt = (instant: dayjs.Dayjs) => offsetItemAt(instant).offset;

  // A transition that affects this wall time is less than a day away.
  const before = offsetAt(naive.subtract(1, 'day'));
  const after = offsetAt(naive.add(1, 'day'));

  const beforeDate = naive.subtract(before, 'minute');
  const afterDate = naive.subtract(after, 'minute');

  const beforeValid = offsetAt(beforeDate) === before;
  const afterValid = offsetAt(afterDate) === after;

  if (beforeValid && (!afterValid || !afterDate.isBefore(beforeDate))) {
    return beforeDate.utcOffset(before);
  }

  if (afterValid) {
    return afterDate.utcOffset(after);
  }

  // The wall time falls into a gap.
  return dayjs(offsetItemAt(naive.add(1, 'day')).date).utcOffset(after);
};

/**
 * Finds the current time zone offset item for a given date from a list of offsets.
 * @param offsets - Array of offset items with associated dates
 * @param currentDate - Date to check against offsets (default is now)
 * @returns The offset item that applies to the current date or null if none found
 */
export const findCurrentOffsetItem = (
  offsets: TOffset[],
  currentDate: dayjs.Dayjs = dayjs(),
): TNullable<TOffset> => {
  for (let i = 0; i < offsets.length; i += 1) {
    const startDate = dayjs(offsets[i].date);
    const endDate = offsets[i + 1]
      ? dayjs(offsets[i + 1].date)
      : dayjs('9999-12-31');

    if (currentDate.isAfter(startDate) && currentDate.isBefore(endDate)) {
      return offsets[i];
    }
  }
  return null;
};

/**
 * Returns the browser's current time zone offset in minutes.
 * Positive values indicate time zones ahead of UTC, negative values indicate behind UTC.
 * @returns Time zone offset in minutes
 */
export const getBrowserOffsetInMinutes = () =>
  new Date().getTimezoneOffset() * -1;

/**
 * Returns the IANA time zone identifier of the user's browser.
 * Example: "America/New_York", "Europe/London"
 * @returns Browser's time zone ID as a string
 */
export const getBrowserTimezoneId = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Checks if a given timestamp is still valid based on an expiration threshold.
 * @param timestamp - The Date object to validate
 * @param expirationMinutes - The validity period in minutes
 * @returns `true` if the timestamp is within the expiration period, otherwise `false`
 */
export const isTimestampValid = (
  timestamp: Date,
  expirationMinutes: number,
) => {
  const elapsedMinutes = (Date.now() - timestamp.getTime()) / 60000;

  return elapsedMinutes < expirationMinutes;
};

/**
 * Returns the ordinal suffix for a given day of the month (e.g., "st", "nd", "rd", "th").
 * @param day - The numeric day of the month
 * @returns The corresponding ordinal suffix as a string
 */
export const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/**
 * Formats a given date into a human-readable string with ordinal suffixes.
 * Supports day, week, and month views.
 * @param date - The date to format
 * @param view - The view type: 'day' | 'week' | 'month' (default: 'day')
 * @returns Formatted date string, e.g., "Jan, 21st", "Feb, 2nd — 8th"
 */
export const formatDate = (date: Date, view: TTimeView = 'day'): string => {
  const dayjsDate = dayjs(date);
  switch (view) {
    case 'day':
      return dayjsDate.format('MMM, Do');
    case 'week': {
      const startOfWeek = dayjsDate.startOf('week');
      const startMonth = startOfWeek.format('MMM');
      const startDay = startOfWeek.date();
      const startSuffix = getOrdinalSuffix(startDay);

      const endOfWeek = startOfWeek.add(6, 'days');
      const endMonth = endOfWeek.format('MMM');
      const endDay = endOfWeek.date();
      const endSuffix = getOrdinalSuffix(endDay);

      if (startMonth === endMonth) {
        return `${startMonth}, ${startDay}${startSuffix} — ${endDay}${endSuffix}`;
      }
      return `${startMonth}, ${startDay}${startSuffix} — ${endMonth}, ${endDay}${endSuffix}`;
    }
    case 'month': {
      const startOfMonth = dayjsDate.startOf('month');
      const startMonth = startOfMonth.format('MMM');
      const startDay = startOfMonth.date();
      const startSuffix = getOrdinalSuffix(startDay);

      const lastDay = dayjsDate.endOf('month');
      return `${startMonth}, ${startDay}${startSuffix} — ${lastDay.date()}${getOrdinalSuffix(lastDay.date())}`;
    }
    default:
      return '';
  }
};

/**
 * Calculates the age in years based on a date of birth.
 * @param dob - Date of birth as a string
 * @returns Age in years
 */
export const calculateAge = (dob: string) => {
  return dayjs().diff(dayjs(dob), 'year');
};

/**
 * Formats a session start date into a readable string with day, month, and time.
 * Example output: "21 Jan, 3:00 PM"
 * @param start - Session start date as a string or Dayjs object
 * @returns Formatted date string
 */
export const formatSessionDate = (start: string | dayjs.Dayjs) => {
  const date = dayjs(start);
  return `${date.format('D MMM')}, ${getTimeFormat(date)}`;
};
/**
 * Formats a session start and end time into a readable string with day, month, and time range.
 * Example output: "21 Jan, 3:00 PM - 3:15 PM"
 * @param start - Session start as a Dayjs object
 * @param end - Session end as a Dayjs object
 * @returns Formatted date and time range string
 */
export const formatSessionDateWithEnd = (
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
) => {
  if (!start || !end) return;
  return `${start.format('MMM')} ${start.date()}, ${getTimeFormat(start)} - ${getTimeFormat(end)}`;
};

/**
 * Calculates the duration between two timestamps in minutes.
 * @param start - Start time as a string, Date, or dayjs object
 * @param end - End time as a string, Date, or dayjs object
 * @param roundUp - If true, rounds up to the nearest whole minute (default: true)
 * @returns Duration in minutes, or null if start or end is invalid
 */
export const getDurationMinutes = (
  start: TDateFormat | undefined,
  end: TDateFormat | undefined,
  roundUp: boolean = true,
) => {
  if (!start || !end) return null;
  const date1 = dayjs(start);
  const date2 = dayjs(end);
  const diffMinutes = date2.diff(date1, 'minute', true);
  return roundUp ? Math.ceil(diffMinutes) : diffMinutes;
};

/**
 * Calculates the time until appointment start and returns the value and unit key.
 * Uses Math.floor for conversion to hours/days (not Math.ceil).
 * Shows only one unit: days (if >= 24h), hours (if >= 1h), or minutes (otherwise).
 * Uses notification creation time instead of current time to keep the value static.
 * @param startTime - Appointment start time as a string, Date, or dayjs object
 * @param createdAt - Notification creation time as a string, Date, or dayjs object
 * @returns Object with timeValue (number) and unitKey (string), or null if startTime or createdAt is invalid
 */
export const getTimeUntilStart = (
  startTime: TDateFormat,
  createdAt: TDateFormat,
) => {
  if (!startTime || !createdAt) return null;

  const notificationTime = dayjs(createdAt).utc();
  const start = dayjs(startTime).utc();
  const differenceInSeconds = start.diff(notificationTime, 'second');
  const differenceInMinutes = differenceInSeconds / 60;
  const minuteDifference = Math.max(1, Math.ceil(differenceInMinutes));

  if (minuteDifference >= MINUTES_IN_DAY) {
    return {
      timeValue: Math.floor(minuteDifference / MINUTES_IN_DAY),
      unitKey: 'account-settings.days',
    };
  }
  if (minuteDifference >= MINUTES_IN_HOUR) {
    return {
      timeValue: Math.floor(minuteDifference / MINUTES_IN_HOUR),
      unitKey: 'account-settings.hours',
    };
  }
  return {
    timeValue: minuteDifference,
    unitKey: 'account-settings.minutes',
  };
};

/**
 * Applies a timezone offset to a given date string based on a user's timezone and a list of available timezones.
 * @param timezones - Array of timezone objects containing offset information
 * @param date - Optional date string to adjust
 * @param myTimezone - Optional user's timezone ID
 * @returns Date string adjusted to the specified timezone
 */
export const applyTimezoneOffset = (
  timezones: TListTimezoneItemCamel[],
  date?: string,
  myTimezone?: string,
) => {
  const offset = getTimeZoneOffsetInMinutes(myTimezone, timezones, date);
  return dayjs(date).utcOffset(offset).toString();
};

/**
 * Generates a formatted list of timezones with current local time for each.
 * @param timezones - Array of timezone objects containing offsets
 * @returns Array of formatted timezone objects including id, title with current time, and offset
 */
export const getTimezoneList = (timezones: TListTimezoneItemCamel[]) =>
  timezones
    ?.reduce<IFormattedTimezone[]>((acc, item) => {
      const currentOffsetItem = findCurrentOffsetItem(item.offsets);
      if (currentOffsetItem) {
        const currentTimeDate = dayjs().utcOffset(currentOffsetItem.offset);
        const currentTime = getTimeFormat(currentTimeDate);
        acc.push({
          id: item.id,
          title: `${item.name} - ${currentTime}`,
          offset: currentOffsetItem.offset,
        });
      }
      return acc;
    }, [])
    .sort((a, b) => a.offset - b.offset) || [];

/**
 * Determines the user's automatic timezone ID based on browser settings.
 * First attempts to match the browser timezone ID with the provided list.
 * Falls back to matching by current browser offset in minutes if ID match is not found.
 * @param timezonesList - Array of formatted timezone objects
 * @returns The matched timezone ID as a string or undefined if no match is found
 */
export const getAutoTimezoneId = (timezonesList: IFormattedTimezone[]) => {
  const autoTimezoneId = getBrowserTimezoneId();
  const findAutoTimezoneById = timezonesList.find(
    ({ id }) => autoTimezoneId === id,
  );
  if (findAutoTimezoneById) {
    return autoTimezoneId;
  }
  const autoTimezoneOffset = getBrowserOffsetInMinutes();
  const findAutoTimezoneIdByOffset = timezonesList.find(
    ({ offset }) => offset === autoTimezoneOffset,
  )?.id;
  return findAutoTimezoneIdByOffset?.toString();
};

/**
 * Converts a duration in seconds to a human-readable stopwatch format.
 * Displays hours, minutes, and seconds as needed.
 * Examples:
 * - 3661 → "1h 1m 1s"
 * - 125 → "2m 5s"
 * - 45 → "45s"
 * - 90000 → "1d 1h 0m"
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export const formatTimeToStopwatch = (seconds: number): string => {
  const duration = dayjs.duration(seconds, 'seconds');
  const months = duration.months();
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();
  const secs = duration.seconds();

  if (months > 0) {
    return `${months}mo${days > 0 ? ` ${days}d` : ''}${hours > 0 ? ` ${hours}h` : ''}${minutes > 0 ? ` ${minutes}m` : ''}`;
  }
  if (days > 0) {
    return `${days}d${hours > 0 ? ` ${hours}h` : ''}${minutes > 0 ? ` ${minutes}m` : ''}`;
  }
  if (hours > 0) {
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}${secs > 0 ? ` ${secs}s` : ''}`;
  }
  if (minutes > 0) {
    return `${minutes}m${secs > 0 ? ` ${secs}s` : ''}`;
  }
  return `${secs}s`;
};

/**
 * Formats a Dayjs date object into a 12-hour time string with AM/PM.
 * Example: 3:05 PM
 * @param date - Dayjs date object
 * @returns Formatted time string in 'hh:mm A' format
 */
export const getTimeFormat = (date: dayjs.Dayjs) => date.format('hh:mm A');

/**
 * Formats a Dayjs date object into a 24-hour time string.
 * Example: 15:05
 * @param date - Dayjs date object
 * @returns Formatted time string in 'HH:mm' format
 */
export const getTime24Format = (date: dayjs.Dayjs) => date.format('HH:mm');

/**
 * Formats a Dayjs date object into an ISO date string (YYYY-MM-DD).
 * Example: 2025-01-21
 * @param date - Dayjs date object
 * @returns Formatted date string in 'YYYY-MM-DD' format
 */
export const getDateOnlyISOFormat = (date: dayjs.Dayjs) =>
  date.format('YYYY-MM-DD');

/**
 * Formats a Dayjs date object into a readable date and time string.
 * Example: 21 Jan, 2025 3:05PM
 * @param date - Dayjs date object
 * @returns Formatted date and time string in 'DD MMM, YYYY hh:mmA' format
 */
export const getDateAndTimeFormat = (date: dayjs.Dayjs) =>
  date.format('DD MMM, YYYY hh:mmA');

/**
 * Formats a Dayjs date object into a short date and time string.
 * Example: 21 Jan, 3:05 PM
 * @param date - Dayjs date object
 * @returns Formatted date and time string in 'D MMM, hh:mm A' format
 */
export const getShortDateAndTimeFormat = (date: dayjs.Dayjs) =>
  date.format('D MMM, hh:mm A');

/**
 * Formats a Dayjs date object to return the full name of the day of the week.
 * Example: "Monday", "Tuesday"
 * @param date - Dayjs date object
 * @returns Full day name as a string
 */
export const getDayFormat = (date: dayjs.Dayjs) => date.format('dddd');

/**
 * Formats a Dayjs date object to a readable date string in "Month Day, Year" format.
 * Example: "January 21, 2025"
 * @param date - Dayjs date object
 * @returns Formatted date string
 */
export const getDateFormat = (date: dayjs.Dayjs) => date.format('MMMM D, YYYY');

/**
 * Formats a Dayjs date object to a short date string in "MM/DD/YYYY" format.
 * Example: "01/21/2025"
 * @param date - Dayjs date object
 * @returns Formatted date string
 */
export const getShortDateFormat = (date: dayjs.Dayjs) => date.format('L');

/**
 * Formats a Dayjs date object to a date and time string in "MM/DD/YYYY hh:mm A" format.
 * Example: "01/21/2025 3:05 PM"
 * @param date - Dayjs date object
 * @returns Formatted date and time string
 */
export const getDateTimeFormat = (date: dayjs.Dayjs) => {
  const shortDate = getShortDateFormat(date);
  const time = getTimeFormat(date);
  return `${shortDate} ${time}`;
};

/**
 * Formats a Dayjs date object to a date and time string with comma in "MM/DD/YYYY, hh:mm A" format.
 * Example: "01/21/2025, 3:05 PM"
 * @param date - Dayjs date object
 * @returns Formatted date and time string with comma
 */
export const getDateTimeWithCommaFormat = (date: dayjs.Dayjs) => {
  const shortDate = getShortDateFormat(date);
  const time = getTimeFormat(date);
  return `${shortDate}, ${time}`;
};

/**
 * Formats a Dayjs date object to a long date and time string in "MMMM D, YYYY h:mm A" format.
 * Example: "January 21, 2025 3:05 PM"
 * @param date - Dayjs date object
 * @returns Formatted long date and time string
 */
export const getLongDateTimeFormat = (date: dayjs.Dayjs) => date.format('LLL');

/**
 * Formats a Dayjs date object to a long date and time string with timezone in "MMMM D, YYYY h:mm A Z" format.
 * Example: "January 21, 2025 3:05 PM EST"
 * @param date - Dayjs date object
 * @returns Formatted long date and time string with timezone
 */
export const getLongDateTimeWithZoneFormat = (date: dayjs.Dayjs) =>
  date.format('LLL Z');

/**
 * Formats a Dayjs date object to a date and time string in 24-hour format "MM/DD/YYYY HH:mm".
 * Example: "01/21/2025 15:05"
 * @param date - Dayjs date object
 * @returns Formatted date and time string in 24-hour format
 */
export const getDateTime24Format = (date: dayjs.Dayjs) =>
  date.format('L HH:mm');

/**
 * Formats a Dayjs date object to a day of week and date string in "dddd, D MMM" format.
 * Example: "Monday, 21 Jan"
 * @param date - Dayjs date object
 * @returns Formatted day and date string
 */
export const getDayAndDateFormat = (date: dayjs.Dayjs) =>
  date.format('dddd, D MMM');

/**
 * Converts a local date string to a UTC ISO string based on the given offset.
 * @param date - Local date string
 * @param offset - Timezone offset in minutes
 * @returns UTC date string in ISO format
 */
export const convertToUTC = (date: string, offset: number): string => {
  return dayjs(date).subtract(offset, 'minute').toISOString();
};

/**
 * Converts the `start` and `end` dates in `start_end_period` of report filter params using a provided UTC conversion function.
 * @param params - Report filter parameters potentially containing a `start_end_period` object
 * @param convertFnUTC - Function to convert a date string to UTC
 * @returns A new `IReportFilterParams` object with `start` and `end` dates converted to UTC
 */
export const processDatesInParams = (
  params: IReportFilterParams,
  convertFnUTC: (date: string) => string,
): IReportFilterParams => {
  const newParams = structuredClone(params);
  if (
    newParams.start_end_period &&
    typeof newParams.start_end_period === 'object' &&
    'start' in newParams.start_end_period &&
    'end' in newParams.start_end_period
  ) {
    newParams.start_end_period.start = convertFnUTC(
      newParams.start_end_period.start,
    );
    newParams.start_end_period.end = convertFnUTC(
      newParams.start_end_period.end,
    );
  }
  return newParams;
};

/**
 * Parses a date string in strict "MM/DD/YYYY" format and returns a JavaScript Date object.
 * @param inputValue - The input date string
 * @returns A `Date` object if the string is valid; otherwise `null`
 */
export const getDateByString = (inputValue: string) => {
  const isStrictFormat = /^\d{2}\/\d{2}\/\d{4}$/.test(inputValue);

  if (!isStrictFormat) return null;

  const parsedDate = dayjs(inputValue, 'L', true);

  if (!parsedDate.isValid()) return null;

  return parsedDate.toDate();
};
